import React, {
    useEffect,
    useRef,
    useState
} from "react";

import Loader from "../loader/loader";
import Pagination from "../Pagination/Pagination";
import AddQASection from "./AddQASection";
import ToastrService from "../../services/toastService";
import {
    useGetAllQASectionsMutation,
    useGetQASectionMutation,
    useDeleteQASectionMutation
} from "./Api";

import {
    Logout,
    shouldLogout
} from "../Common/Common";

import {
    useNavigate
} from "react-router-dom";

import {
    FaEdit,
    FaPlus,
    FaSearch,
    FaTrash
} from "react-icons/fa";

const QASectionManagement = () => {

    const navigate = useNavigate();

    const [
        getAllSectionsApi,
        {
            isLoading
        }
    ] = useGetAllQASectionsMutation();

    const [
    deleteQASectionApi,
    {
        isLoading: deleteSectionLoading
    }
] = useDeleteQASectionMutation();

    const [
        getSectionByIdApi,
        {
            isLoading: getSectionLoading
        }
    ] = useGetQASectionMutation();

    const [
        sections,
        setSections
    ] = useState([]);

    const [
        currentPage,
        setCurrentPage
    ] = useState(1);

    const [
        rowsPerPage,
        setRowsPerPage
    ] = useState(10);

    const [
        totalRecords,
        setTotalRecords
    ] = useState(0);

    const [
        totalPages,
        setTotalPages
    ] = useState(1);

    const [
        statusFilter,
        setStatusFilter
    ] = useState("");

    const [
        search,
        setSearch
    ] = useState("");

    const [
        debouncedSearch,
        setDebouncedSearch
    ] = useState("");

    const [
        refresh,
        setRefresh
    ] = useState(false);

    /*
     * Common drawer state for:
     * add and edit
     */
    const [
        isSectionDrawerOpen,
        setIsSectionDrawerOpen
    ] = useState(false);

    const [
        sectionDrawerMode,
        setSectionDrawerMode
    ] = useState("add");

    const [
        selectedSection,
        setSelectedSection
    ] = useState(null);

    const requestIdRef =
        useRef(0);

    /*
     * Search debounce
     */
    useEffect(() => {

        const timer =
            setTimeout(() => {

                setDebouncedSearch(
                    search.trim()
                );

            }, 700);

        return () =>
            clearTimeout(timer);

    }, [
        search
    ]);

    /*
     * Reset pagination when search
     * or status changes
     */
    useEffect(() => {

        setCurrentPage(1);

    }, [
        debouncedSearch,
        statusFilter
    ]);

    /*
     * Get sections
     */
    useEffect(() => {

        getAllSections();

    }, [
        currentPage,
        rowsPerPage,
        statusFilter,
        debouncedSearch,
        refresh
    ]);

    /*
     * Section list API
     */
    const getAllSections = async () => {

        try {

            const requestId =
                ++requestIdRef.current;

            const response =
                await getAllSectionsApi({
                    page: currentPage,
                    limit: rowsPerPage,
                    status: statusFilter,
                    search: debouncedSearch
                });

            /*
             * Ignore old API response
             */
            if (
                requestId !==
                requestIdRef.current
            ) {
                return;
            }

            if (
                response?.data?.success
            ) {

                const responseData =
                    response?.data?.data;

                setSections(
                    responseData?.sections ||
                    []
                );

                setTotalRecords(
                    Number(
                        responseData
                            ?.pagination
                            ?.total || 0
                    )
                );

                setTotalPages(
                    Number(
                        responseData
                            ?.pagination
                            ?.totalPages || 1
                    )
                );

                return;

            }

            if (
                shouldLogout(
                    response?.error
                )
            ) {
                Logout(navigate);
            }

        } catch (error) {

            console.error(
                "Get QA sections error:",
                error
            );

        }

    };

    /*
     * Get section details API
     *
     * Use this before opening the
     * edit drawer.
     */
    const getSectionById = async (
        id
    ) => {

        try {

            if (!id) {
                return;
            }

            const response =
                await getSectionByIdApi({
                    id
                });

            if (
                response?.data?.success
            ) {

                const sectionData =
                    response?.data?.data;

                setSelectedSection(
                    sectionData
                );

                setSectionDrawerMode(
                    "edit"
                );

                setIsSectionDrawerOpen(
                    true
                );

                return;

            }

            if (
                shouldLogout(
                    response?.error
                )
            ) {
                Logout(navigate);
            }

        } catch (error) {

            console.error(
                "Get QA section error:",
                error
            );

        }

    };

    /*
     * Open add drawer
     */
    const handleCreateSection = () => {

        setSectionDrawerMode(
            "add"
        );

        setSelectedSection(
            null
        );

        setIsSectionDrawerOpen(
            true
        );

    };

    /*
     * Open edit drawer
     *
     * Recommended:
     * fetch full section details
     * using the ID.
     */
    const handleEditSection = (
        section
    ) => {
        setSectionDrawerMode("edit");
        setSelectedSection(section);
        setIsSectionDrawerOpen(true);
    };
const handleDeleteSection = async (
    section
) => {

    try {

        const confirmed =
            window.confirm(
                `Are you sure you want to delete "${section.section_name}"?`
            );

        if (!confirmed) {
            return;
        }

        const response =
            await deleteQASectionApi({
                id: section.id
            });

        if (
            response?.data?.success
        ) {

            ToastrService.success(
                response.data.message ||
                "QA section deleted successfully"
            );

            /*
             * If deleting last record
             * on current page, move back.
             */
            if (
                sections.length === 1 &&
                currentPage > 1
            ) {
                setCurrentPage(
                    currentPage - 1
                );
            } else {
                setRefresh(
                    value => !value
                );
            }

            return;
        }

        if (
            shouldLogout(
                response?.error
            )
        ) {
            Logout(
                navigate
            );

            return;
        }

        ToastrService.error(
            response?.error?.data?.message ||
            response?.data?.message ||
            "Unable to delete QA section"
        );

    } catch (error) {

        console.error(
            "Delete QA section error:",
            error
        );

        ToastrService.error(
            error?.data?.message ||
            error?.message ||
            "Unable to delete QA section"
        );

    }

};
    /*
     * When table data already contains
     * all section fields, this simpler
     * edit function can be used instead.
     */
    // const handleEditSection = (
    //     section
    // ) => {
    //
    //     setSectionDrawerMode(
    //         "edit"
    //     );
    //
    //     setSelectedSection(
    //         section
    //     );
    //
    //     setIsSectionDrawerOpen(
    //         true
    //     );
    //
    // };

    /*
     * Close common drawer
     */
    const handleCloseSectionDrawer =
        () => {

            setIsSectionDrawerOpen(
                false
            );

            setSectionDrawerMode(
                "add"
            );

            setSelectedSection(
                null
            );

        };

    /*
     * Called after add or update
     */
    const handleSave = () => {

        handleCloseSectionDrawer();

        /*
         * Trigger list refresh.
         */
        setRefresh(
            (value) => !value
        );

    };

    const startEntry =
        totalRecords === 0
            ? 0
            : (
                currentPage - 1
            ) *
            rowsPerPage +
            1;

    const endEntry =
        Math.min(
            currentPage *
            rowsPerPage,
            totalRecords
        );

    const getStatusBadge = (
        status
    ) => {

        const isActive =
            status === "active" ||
            status === 1 ||
            status === true;

        return (
            <span
                className={
                    isActive
                        ? "badge bg-soft-success text-success"
                        : "badge bg-soft-danger text-danger"
                }
            >
                {
                    isActive
                        ? "Active"
                        : "Inactive"
                }
            </span>
        );

    };

    const pageLoading =
        isLoading ||
        getSectionLoading;

    return (
        <div>

            {
                pageLoading
                    ? (
                        <Loader />
                    )
                    : (
                        <div>

                            {/* Page header */}

                            <div className="page-header">

                                <div className="page-header-left d-flex align-items-center">

                                    <ul className="breadcrumb mb-0">

                                        <li className="breadcrumb-item">
                                            QA Management
                                        </li>

                                        <li className="breadcrumb-item active">
                                            QA Sections
                                        </li>

                                    </ul>

                                </div>

                            </div>

                            {/* Main content */}

                            <div className="main-content">

                                <div className="row">

                                    <div className="p-6">

                                        {/* Title */}

                                        <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">

                                            <div>

                                                <h3 className="h4 mb-1 drawer-header">
                                                    QA Section Management
                                                </h3>

                                                <p className="mb-0 text-muted">
                                                    Manage QA evaluation sections, weightage and display order
                                                </p>

                                            </div>

                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                onClick={
                                                    handleCreateSection
                                                }
                                            >
                                                <FaPlus className="me-2" />

                                                Create Section
                                            </button>

                                        </div>

                                        <div className="card p-3 mb-4">

                                            {/* Filters */}

                                            <div className="card shadow-sm border-0 p-3 mb-4">

                                                <div className="row justify-content-end g-3">

                                                    {/* Search */}

                                                    <div className="col-12 col-sm-6 col-md-4 col-lg-3">

                                                        <div className="position-relative">

                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm ps-5"
                                                                placeholder="Search section by name..."
                                                                value={
                                                                    search
                                                                }
                                                                onChange={
                                                                    (
                                                                        event
                                                                    ) =>
                                                                        setSearch(
                                                                            event
                                                                                .target
                                                                                .value
                                                                        )
                                                                }
                                                            />

                                                        </div>

                                                    </div>

                                                    {/* Status */}

                                                    <div className="col-12 col-sm-6 col-md-3 col-lg-2">

                                                        <select
                                                            className="form-select form-select-sm"
                                                            value={
                                                                statusFilter ||
                                                                "all"
                                                            }
                                                            onChange={
                                                                (
                                                                    event
                                                                ) =>
                                                                    setStatusFilter(
                                                                        event
                                                                            .target
                                                                            .value ===
                                                                            "all"
                                                                            ? ""
                                                                            : event
                                                                                .target
                                                                                .value
                                                                    )
                                                            }
                                                        >
                                                            <option value="all">
                                                                All Status
                                                            </option>

                                                            <option value="active">
                                                                Active
                                                            </option>

                                                            <option value="inactive">
                                                                Inactive
                                                            </option>

                                                        </select>

                                                    </div>

                                                </div>

                                            </div>

                                            {/* Table */}

                                            <div className="table-responsive">

                                                <table className="table table-hover align-middle mb-0">

                                                    <thead>

                                                        <tr>

                                                            <th width="70">
                                                                #
                                                            </th>

                                                            <th>
                                                                Section Name
                                                            </th>
                                                            <th>
                                                                Section Slug
                                                            </th>

                                                            <th className="text-center">
                                                                Weightage
                                                            </th>

                                                            <th className="text-center">
                                                                Questions
                                                            </th>

                                                            <th className="text-center">
                                                                Display Order
                                                            </th>

                                                            <th className="text-center">
                                                                Status
                                                            </th>

                                                            <th
                                                                className="text-center"
                                                                width="100"
                                                            >
                                                                Actions
                                                            </th>

                                                        </tr>

                                                    </thead>

                                                    <tbody>

                                                        {
                                                            sections.length >
                                                                0
                                                                ? (
                                                                    sections.map(
                                                                        (
                                                                            item,
                                                                            index
                                                                        ) => (
                                                                            <tr
                                                                                key={
                                                                                    item.id
                                                                                }
                                                                            >

                                                                                <td>
                                                                                    {
                                                                                        (
                                                                                            currentPage -
                                                                                            1
                                                                                        ) *
                                                                                        rowsPerPage +
                                                                                        index +
                                                                                        1
                                                                                    }
                                                                                </td>

                                                                                <td>

                                                                                    <div className="fw-semibold text-dark">
                                                                                        {
                                                                                            item.section_name
                                                                                        }
                                                                                    </div>

                                                                                    {
                                                                                        Boolean(
                                                                                            item.is_mandatory
                                                                                        ) && (
                                                                                            <small className="text-danger">
                                                                                                Mandatory section
                                                                                            </small>
                                                                                        )
                                                                                    }

                                                                                </td>

                                                                                <td>

                                                                                    <div className="fw-semibold text-dark">
                                                                                        {
                                                                                            item.section_slug
                                                                                        }
                                                                                    </div>
                                                                                </td>

                                                                                <td className="text-center">

                                                                                    <span className="badge bg-soft-primary text-primary">
                                                                                        {
                                                                                            item.weightage
                                                                                        }
                                                                                        %
                                                                                    </span>

                                                                                </td>

                                                                                <td className="text-center">
                                                                                    {
                                                                                        item.question_count ??
                                                                                        0
                                                                                    }
                                                                                </td>

                                                                                <td className="text-center">
                                                                                    {
                                                                                        item.display_order
                                                                                    }
                                                                                </td>

                                                                                <td className="text-center">
                                                                                    {
                                                                                        getStatusBadge(
                                                                                            item.status
                                                                                        )
                                                                                    }
                                                                                </td>

                                                                                <td className="text-center">

                                                                                    <div className="d-inline-flex gap-2">

                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-sm btn-link text-dark p-0"
                                                                                            title="Edit Section"
                                                                                            onClick={() => handleEditSection(item)}
                                                                                        >
                                                                                            <FaEdit
                                                                                                size={
                                                                                                    16
                                                                                                }
                                                                                            />
                                                                                        </button>

                                                                                        <button
                                                                                            type="button"
                                                                                            className="btn btn-sm btn-link text-dark p-0"
                                                                                            title="Delete section"
                                                                                            onClick={() => handleDeleteSection(item)}
                                                                                        >
                                                                                            <FaTrash size={18} />
                                                                                        </button>

                                                                                    </div>
                                                                                
                                                                                </td>


                                                                            </tr>
                                                                        )
                                                                    )
                                                                )
                                                                : (
                                                                    <tr>

                                                                        <td
                                                                            colSpan="8"
                                                                            className="text-center text-muted py-4"
                                                                            style={{
                                                                                fontStyle:
                                                                                    "italic",
                                                                                backgroundColor:
                                                                                    "#f9f9f9"
                                                                            }}
                                                                        >
                                                                            No QA sections found
                                                                        </td>

                                                                    </tr>
                                                                )
                                                        }

                                                    </tbody>

                                                </table>

                                                {/* Pagination */}

                                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 gap-2">

                                                    <div className="d-flex align-items-center gap-1 small">

                                                        <span>
                                                            Show
                                                        </span>

                                                        <select
                                                            className="form-select form-select-sm page-size-select"
                                                            value={
                                                                rowsPerPage
                                                            }
                                                            onChange={
                                                                (
                                                                    event
                                                                ) => {

                                                                    setRowsPerPage(
                                                                        Number(
                                                                            event
                                                                                .target
                                                                                .value
                                                                        )
                                                                    );

                                                                    setCurrentPage(
                                                                        1
                                                                    );

                                                                }
                                                            }
                                                        >
                                                            <option value={5}>
                                                                5
                                                            </option>

                                                            <option value={10}>
                                                                10
                                                            </option>

                                                            <option value={15}>
                                                                15
                                                            </option>

                                                            <option value={25}>
                                                                25
                                                            </option>

                                                        </select>

                                                        <span>
                                                            entries
                                                        </span>

                                                    </div>

                                                    <div className="small text-muted">

                                                        Showing{" "}
                                                        {startEntry}{" "}
                                                        to{" "}
                                                        {endEntry}{" "}
                                                        of{" "}
                                                        {totalRecords}{" "}
                                                        entries

                                                    </div>

                                                    <Pagination
                                                        currentPage={
                                                            currentPage
                                                        }
                                                        totalPages={
                                                            totalPages
                                                        }
                                                        maxPagesToShow={
                                                            10
                                                        }
                                                        onPageChange={
                                                            (
                                                                page
                                                            ) =>
                                                                setCurrentPage(
                                                                    page
                                                                )
                                                        }
                                                    />

                                                </div>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                            </div>

                            {/* Common add/edit drawer */}

                            {
                                isSectionDrawerOpen && (
                                    <AddQASection
                                        mode={
                                            sectionDrawerMode
                                        }
                                        data={
                                            selectedSection
                                        }
                                        onClose={
                                            handleCloseSectionDrawer
                                        }
                                        onSave={
                                            handleSave
                                        }
                                    />
                                )
                            }

                        </div>
                    )
            }

        </div>
    );

};

export default QASectionManagement;